// timezone-capture — pure, testable helpers for the "capture the workspace
// timezone from the browser, set-if-empty" feature (ships with/before the BE#648
// DST release). The decision lives here as a pure function so the bootstrap hook
// stays trivial and the contract is unit-tested in isolation.
//
// The IANA validation + canonicalisation MIRRORS the backend
// (capture-workspace-timezone.ts): both sides accept a canonical IANA region name
// OR an IANA ALIAS (e.g. `Asia/Kolkata`, `Europe/Kyiv`) that Firefox/Safari
// report, normalise the alias to the engine's canonical name, and reject bare
// offsets, every `Etc/*` alias (`Etc/GMT±N`, `Etc/UTC`), `Etc/Unknown` and `UTC`.
// No guessing from offsets. The backend is authoritative and re-canonicalises
// what it receives; this just avoids a pointless round-trip and sends the
// already-canonical form.

let ianaZoneSet: Set<string> | undefined;
const getIanaZoneSet = (): Set<string> => {
  if (!ianaZoneSet) {
    try {
      ianaZoneSet = new Set<string>(
        (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
          'timeZone',
        ) ?? [],
      );
    } catch {
      // Extremely old engine without supportedValuesOf → empty set → we simply
      // never capture (the backend would reject an unvalidated value anyway).
      ianaZoneSet = new Set<string>();
    }
  }
  return ianaZoneSet;
};

/** Offset-like strings we never treat as a zone ("no guessing from offsets"). */
const OFFSET_LIKE = /^(?:GMT|UTC)?[+-]\d/i;

/**
 * Canonicalise a timezone string to its canonical IANA region name, or null if
 * it is not a real region zone. Accepts IANA aliases and normalises them; rejects
 * non-strings, bare offsets, `Etc/*` (incl. `Etc/GMT±N` / `Etc/UTC` /
 * `Etc/Unknown`), `UTC`, and anything `Intl` cannot resolve. Mirrors the backend.
 */
export const canonicalizeIanaTimezone = (tz: unknown): string | null => {
  if (typeof tz !== 'string') return null;
  const trimmed = tz.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('Etc/')) return null;
  if (OFFSET_LIKE.test(trimmed)) return null;

  let canonical: string;
  try {
    // Resolving validates the zone (unknown zone throws) AND maps alias→canonical.
    canonical = Intl.DateTimeFormat('en-US', { timeZone: trimmed }).resolvedOptions().timeZone;
  } catch {
    return null;
  }

  if (!canonical || canonical.startsWith('Etc/') || canonical === 'UTC') return null;
  // Offsets resolve to themselves and are not in the canonical set → rejected.
  if (!getIanaZoneSet().has(canonical)) return null;
  return canonical;
};

/**
 * True only for a resolvable IANA region zone (canonical or alias). Rejects
 * non-strings, bare offsets, `Etc/*`, `Etc/Unknown`, `UTC` and unknown strings.
 */
export const isValidIanaTimezone = (tz: unknown): tz is string =>
  canonicalizeIanaTimezone(tz) !== null;

/**
 * Decide whether to capture the browser timezone for the current workspace.
 * Returns the CANONICAL timezone to persist ONLY when:
 *   - the workspace has no timezone yet (empty/absent/whitespace), AND
 *   - the browser's Intl timezone resolves to a valid canonical IANA name.
 * Otherwise returns null (never overwrite a set value; never persist a bad zone).
 */
export const shouldCaptureTimezone = (
  currentWorkspaceTz: string | null | undefined,
  intlTz: string | null | undefined,
): string | null => {
  const workspaceHasTz = typeof currentWorkspaceTz === 'string' && currentWorkspaceTz.trim() !== '';
  if (workspaceHasTz) return null;
  return canonicalizeIanaTimezone(intlTz);
};

/** The browser's resolved IANA timezone, or undefined if unavailable. */
export const getBrowserTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};
